import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../pharmacy/HeaderBar";
import { Package, X } from "lucide-react";
import CustomerSelectionModal from "../pharmacy/CustomerSelectionModal";
import PatientSelectionModal from "../pharmacy/PatientSelectionModal";
import ProductSelectionModal from "../pharmacy/ProductSelectionModal";
import ItemsTable from "../pharmacy/ItemsTable";
import SummaryPanel from "../pharmacy/SummaryPanel";
import { calculateItemAmount, computeTotals } from "../pharmacy/Utility/calc";
import AddPaymentModal from "../pharmacy/pharmacymodels/AddPaymentModal";
import toast, { Toaster } from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAddPharmacyOrderMutation } from "../../State/Api/PharmacyOrderApiSlice";

export const SAMPLE_CUSTOMERS = [
  {
    id: "c1",
    name: "Sample Customer",
    address: "Sector 37, Gurgaon, Haryana",
    type: "Customer",
    mobile: "9999888877",
    relation: "Self",
    discount: 10,
  },
  {
    id: "c2",
    name: "Jffrdtuju",
    address: "khbkbhkrjj",
    relation: "Customer",
    mobile: "7894561235",
    discount: 15,
  },
  {
    id: "c3",
    name: "Cash Sale",
    address: "jvgtjhchjh",
    relation: "Customer",
    mobile: "7894561235",
    discount: 85,
  },
  {
    id: "c4",
    name: "Hari",
    address: "jgvjgvjvjvjv",
    relation: "Customer",
    mobile: "9876543210",
    discount: 68,
  },
];

export const SAMPLE_PATIENTS = [
  {
    id: "c1",
    name: "Sample Patients",
    address: "Sector 37, Gurgaon, Haryana",
    mobile: "9999888877",
    gender: "Male",
    discount: 10,
  },
  {
    id: "c2",
    name: "Ram",
    address: "khbkbhkrjj",
    gender: "Female",
    mobile: "7894561235",
    discount: 15,
  },
  {
    id: "c3",
    name: "Cash Sale",
    address: "dskddddswd",
    gender: "Male",
    mobile: "7894561235",
    discount: 85,
  },
  {
    id: "c4",
    name: "Hari",
    address: "jgvjgvjvjvjv",
    gender: "Female",
    mobile: "9876543210",
    discount: 68,
  },
];

export const SAMPLE_PRODUCTS = [
  {
    id: "p1",
    name: "Dabur Tulsi Drop 30ml",
    company: "Dabur India Ltd",
    pack: "30ml",
    mrp: 182.81,
    saleRate: 182.81,
    gst: 5,
  },
  {
    id: "p2",
    name: "Tabi Tab",
    company: "Dr Reddys Laboratories Ltd",
    pack: "30's",
    mrp: 1217.81,
    saleRate: 1217.81,
    gst: 12,
  },
  {
    id: "p3",
    name: "Tayo Tab",
    company: "Eris Lifesciences Ltd",
    pack: "15's",
    mrp: 138.85,
    saleRate: 138.85,
    gst: 12,
  },
  {
    id: "p4",
    name: "Tavin Tab",
    company: "Emcure Pharmaceuticals Ltd",
    pack: "30's",
    mrp: 1295.81,
    saleRate: 1295.81,
    gst: 12,
  },
];

export default function PharmacyOrders() {
  const [customerModal, setCustomerModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [patient, setPatient] = useState("");
  const [deliveryHome, setDeliveryHome] = useState(true);
  const [notify, setNotify] = useState(false);
  const [items, setItems] = useState([]);
  const [patientModal, setPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);

  const navigate = useNavigate();

  const PharmaId = localStorage.getItem("userUniqueID");

  const [addPharmacyOrder, { isLoading }] = useAddPharmacyOrderMutation();

  const addItem = (p) => {
    const qty = 1;
    const saleRate = p.saleRate ?? p.mrp ?? 0;
    const gstPct = p.gst ?? 5;
    const discount = 0;
    const amount = calculateItemAmount(saleRate, qty, discount, gstPct);

    setItems((prev) => {
      const ex = prev.find((x) => x.id === (p.productID || p.id));
      if (ex) {
        return prev.map((x) =>
          x.id === (p.productID || p.id)
            ? {
                ...x,
                qty: x.qty + qty,
                amount: calculateItemAmount(
                  saleRate,
                  x.qty + qty,
                  x.discount,
                  x.gstPct
                ),
              }
            : x
        );
      }

      return [
        ...prev,
        {
          id: p.productID || p.id,
          productname: p.productName || p.name || "Unnamed Product",
          company: p.cmpnyName || p.company || "-",
          pack: p.unitsPerPack || p.pack || "-",
          qty,
          mrp: p.mrp ?? saleRate,
          saleRate,
          discount,
          gstPct,
          amount,
        },
      ];
    });
  };

  const updateQty = (id, qty) =>
    setItems((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              qty,
              amount: calculateItemAmount(
                x.saleRate,
                qty,
                x.discount,
                x.gstPct
              ),
            }
          : x
      )
    );

  const updateDiscount = (id, discount) =>
    setItems((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              discount,
              amount: calculateItemAmount(
                x.saleRate,
                x.qty,
                discount,
                x.gstPct
              ),
            }
          : x
      )
    );

  const updateGst = (id, gstPct) => {
    setItems((prev) =>
      prev.map((x) =>
        x.id === id
          ? {
              ...x,
              gstPct,
              amount: calculateItemAmount(
                x.saleRate,
                x.qty,
                x.discount,
                gstPct
              ),
            }
          : x
      )
    );
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((x) => x.id !== id));

  const totals = useMemo(() => computeTotals(items), [items]);

  const formik = useFormik({
    initialValues: {
      customerName: "",
      poReference: "",
    },
    validationSchema: Yup.object({
      customerName: Yup.string().required("Customer name is required"),
    }),
    onSubmit: async (values) => {
      if (items.length === 0) {
        toast.error("Please add at least one product!", {
          position: "top-center",
          style: { background: "#ef4444", color: "#fff", fontWeight: 600 },
        });
        return;
      }

      try {
        const payload = {
          customerBillID: 0,
          pharmaID: PharmaId,      
            patientID: selectedPatient?.patientID || null,
          poReference: values.poReference || "",
          deliveryType: deliveryHome ? "Home Delivery" : "Pickup",
          notifyIfReady: notify,
          totalItems: items.length,
          taxableAmount: parseFloat(totals.taxableAmount) || 0,
          taxAmount: parseFloat(totals.gstAmount) || 0,
          roundOff: parseFloat(totals.roundOff || 0),
          grandTotal: parseFloat(totals.totalAmount) || 0,
          paymentType: "Cash",
          isActive: true,
          createdBy: "user",
          updatedBy: "user",
          products: items.map((item) => ({
            customerBillProdID: 0,
            pharmaID: PharmaId,
            productid: item?.id || null,
            customerBillID: 0,
            batchID: null,
            mrp: parseFloat(item.mrp) || 0,
            saleRate: parseFloat(item.saleRate) || 0,
            quantity: parseInt(item.qty) || 0,
            discPercent: parseFloat(item.discount) || 0,
            gstPercent: parseFloat(item.gstPct) || 0,
            amount: parseFloat(item.amount) || 0,
            isActive: true,
          })),
        };

        console.log("📦 Final Order Payload:", payload);
        await addPharmacyOrder(payload).unwrap();

        toast.success("Pharmacy Order Saved Successfully!", {
          position: "top-center",
          style: { background: "#10b981", color: "#fff", fontWeight: 600 },
        });

        navigate("/PharmacyCustomerorders");
      } catch (error) {
        console.error("❌ Error while saving order:", error);
        toast.error("Failed to save order!", {
          position: "top-center",
          style: { background: "#ef4444", color: "#fff", fontWeight: 600 },
        });
      }
    },
  });

  const onSave = async () => {
    if (items.length === 0) {
      toast.error("Please add at least one product", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#ef4444",
          color: "#fff",
          fontWeight: 600,
          fontSize: "14px",
          padding: "12px 20px",
          borderRadius: "8px",
        },
        icon: "⚠️",
      });
      return;
    }

    if (!selectedPatient) {
      toast.error("Please select a patient", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#ef4444",
          color: "#fff",
          fontWeight: 600,
          fontSize: "14px",
          padding: "12px 20px",
          borderRadius: "8px",
        },
        icon: "👤",
      });
      return;
    }

    try {
      const payload = {
        customerBillID: 0,
        pharmaID: PharmaId,
        patientID: selectedPatient?.patientID || null,
        poReference: formik.values.poReference || "",
        deliveryType: deliveryHome ? "Home Delivery" : "Pickup",
        notifyIfReady: notify,
        totalItems: items.length,
        taxableAmount: parseFloat(totals.taxableAmount) || 0,
        taxAmount: parseFloat(totals.gstAmount) || 0,
        roundOff: parseFloat(totals.roundOff || 0),
        grandTotal: parseFloat(totals.totalAmount) || 0,
        paymentType: "Cash",
        isActive: true,
        createdBy: "user",
        updatedBy: "user",
        products: items.map((item) => ({
          customerBillProdID: 0,
          pharmaID: PharmaId,
          productid: item?.id || null,
          customerBillID: 0,
          batchID: null,
          mrp: parseFloat(item.mrp) || 0,
          saleRate: parseFloat(item.saleRate) || 0,
          quantity: parseInt(item.qty) || 0,
          discPercent: parseFloat(item.discount) || 0,
          gstPercent: parseFloat(item.gstPct) || 0,
          amount: parseFloat(item.amount) || 0,
          isActive: true,
        })),
      };

      console.log("📦 Saving order...", payload);

      await addPharmacyOrder(payload).unwrap();

      toast.success("Pharmacy Order Saved Successfully!", {
        position: "top-center",
        style: { background: "#10b981", color: "#fff", fontWeight: 600 },
      });

      setPaymentModal(true);
    } catch (error) {
      console.error("❌ Error while saving order:", error);
      toast.error("Failed to save order!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 600 },
      });
    }
  };

  const handlePaymentSubmit = (paymentData) => {
    console.log("Order Data:", {
      patient: selectedPatient,
      items,
      totals,
      payment: paymentData,
    });

    // Navigate to the customer orders page with state to trigger toast
    navigate("/PharmacyCustomerorders", {
      state: { paymentSuccess: true, paymentData },
    });
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <Toaster />

      {/* <CustomerSelectionModal
        open={customerModal}
        onClose={() => setCustomerModal(false)}
        onSelect={setSelectedCustomer}
        data={SAMPLE_CUSTOMERS}
      /> */}
      <form onSubmit={formik.handleSubmit}>
        <PatientSelectionModal
          open={patientModal}
          onClose={() => setPatientModal(false)}
          onSelect={setSelectedPatient}
          data={SAMPLE_PATIENTS}
        />

        <AddPaymentModal
          open={paymentModal}
          onClose={() => setPaymentModal(false)}
          totalPayable={totals?.payable || 0}
          onSubmit={handlePaymentSubmit}
        />

        <ProductSelectionModal
          open={productModal}
          onClose={() => setProductModal(false)}
          onSelect={addItem}
          data={SAMPLE_PRODUCTS}
        />

        <div style={{ background: "#fff", padding: "12px 20px" }}>
          <div
            style={{
              maxWidth: "1600px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#10b981",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Package size={18} color="#fff" />
              </div>
              <div>
                <h1
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    margin: 0,
                    color: "#1f2937",
                  }}
                >
                  Pharmacy Order
                </h1>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#999",
                    margin: "2px 0 0 0",
                  }}
                >
                  Create new customer order
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "2%",
                width: "8rem",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className="gradient-button"
                onClick={onSave}
              >
                Save Order
              </button>
              <button
                onClick={() => window.history.back()}
                style={{
                  background: "#fee2e2",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#fecaca")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fee2e2")
                }
              >
                <X size={18} color="#dc2626" />
              </button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "20px" }}>
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              padding: "20px",
            }}
          >
            {/* Customer info row */}
            <div
              style={{
                padding: "20px",
                borderBottom: "1px solid #e2e8f0",
                background: "#fafbfc",
              }}
            >
              <h2
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1f2937",
                  margin: "0 0 12px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Customer Information
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#374151",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    Patient Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <button
                    onClick={() => setPatientModal(true)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      textAlign: "left",
                      background: selectedPatient ? "#f0fdf4" : "#fff",
                      cursor: "pointer",
                      color: selectedPatient ? "#1f2937" : "#999",
                      fontWeight: selectedPatient ? 600 : 400,
                    }}
                  >
                    {selectedPatient
                      ? selectedPatient.patientName
                      : "Select patient name"}
                  </button>
                </div>

                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#374151",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    PO Reference
                  </label>
                  <input
                    name="poReference"
                    value={formik.values.poReference}
                    onChange={formik.handleChange}
                    placeholder="Optional reference number"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#10b981")}
                    onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    gap: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      cursor: "pointer",
                      fontWeight: 500,
                      color: "#374151",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={deliveryHome}
                      onChange={(e) => setDeliveryHome(e.target.checked)}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: "#10b981",
                      }}
                    />
                    Home Delivery
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      cursor: "pointer",
                      fontWeight: 500,
                      color: "#374151",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={notify}
                      onChange={(e) => setNotify(e.target.checked)}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: "#10b981",
                      }}
                    />
                    Notify When Ready
                  </label>
                </div>
              </div>
            </div>

            {/* Product select trigger */}
            <div style={{ padding: "20px", borderBottom: "1px solid #e2e8f0" }}>
              <h2
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1f2937",
                  margin: "0 0 12px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Add Products
              </h2>

              <button
                onClick={() => setProductModal(true)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px dashed #10b981",
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: "#f0fdf4",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: 600,
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#d1fae5";
                  e.currentTarget.style.borderStyle = "solid";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f0fdf4";
                  e.currentTarget.style.borderStyle = "dashed";
                }}
              >
                + Click to Select Product
              </button>
            </div>

            <ItemsTable
              items={items}
              onQty={updateQty}
              onDiscount={updateDiscount}
              onGst={updateGst}
              onRemove={removeItem}
            />

            {items.length > 0 && <SummaryPanel totals={totals} />}
          </div>
        </div>
      </form>
    </div>
  );
}
